import { Link, MessageSquareText, Send } from 'lucide-react';
import React from 'react';
import { toast } from 'sonner';
import {
  extractReferralCode,
  formatExpiryDate,
  interpolateReferralMessage
} from '../../utils/interpolateReferralMessage';
import { Drawer, DrawerContent } from '../ui/drawer';

interface ShareReferralDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  referralLink: string;
  customMessage?: string | null;
  /** Displayed as {{RewardValue}} — e.g. "50 points" */
  rewardValue?: string;
  /** ISO date string for {{ExpiryDate}} */
  expiryDate?: string | null;
}

interface ShareAction {
  title: string;
  onClick: () => void;
  icon?: React.FC<{ className?: string }>;
  image?: string;
}

const ShareReferralDrawer: React.FC<ShareReferralDrawerProps> = ({
  open,
  onOpenChange,
  referralLink,
  customMessage,
  rewardValue,
  expiryDate
}) => {
  const referralCode = extractReferralCode(referralLink);

  const interpolatedMessage = customMessage
    ? interpolateReferralMessage(customMessage, {
        ReferralLink: referralLink,
        ReferralCode: referralCode,
        RewardValue: rewardValue,
        ExpiryDate: formatExpiryDate(expiryDate)
      })
    : null;

  const fullMessage = interpolatedMessage
    ? interpolatedMessage.includes(referralLink)
      ? interpolatedMessage
      : `${interpolatedMessage}\n${referralLink}`
    : referralLink;

  const actions: ShareAction[] = [
    {
      title: 'Copy link',
      icon: Link,
      onClick: () => {
        navigator.clipboard.writeText(referralLink);
        toast.success('Link copied to clipboard!');
        onOpenChange(false);
      }
    },
    ...(interpolatedMessage
      ? [
          {
            title: 'Copy message',
            icon: MessageSquareText,
            onClick: () => {
              navigator.clipboard.writeText(fullMessage);
              toast.success('Message copied to clipboard!');
              onOpenChange(false);
            }
          } as ShareAction,
          {
            title: 'WhatsApp',
            image: '/assets/images/whatsapp.png',
            onClick: () => {
              window.open(
                `https://wa.me/?text=${encodeURIComponent(fullMessage)}`,
                '_blank'
              );
              onOpenChange(false);
            }
          } as ShareAction,
          {
            title: 'Telegram',
            image: '/assets/images/telegram.png',
            onClick: () => {
              window.open(
                `https://t.me/share/url?url=${encodeURIComponent(referralLink)}&text=${encodeURIComponent(interpolatedMessage)}`,
                '_blank'
              );
              onOpenChange(false);
            }
          } as ShareAction,
          {
            title: 'Instagram',
            image: '/assets/images/instagram.png',
            onClick: () => {
              navigator.clipboard.writeText(fullMessage);
              toast.success('Message copied — paste it on Instagram!');
              onOpenChange(false);
            }
          } as ShareAction,
          {
            title: 'SMS',
            icon: Send,
            onClick: () => {
              window.open(
                `sms:?&body=${encodeURIComponent(fullMessage)}`,
                '_self'
              );
              onOpenChange(false);
            }
          } as ShareAction
        ]
      : [])
  ];

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent>
        <div className="hide-scrollbar mx-auto flex w-full flex-row overflow-auto px-4 pb-8 pt-2">
          {actions.map((action, index) => (
            <div
              key={index}
              className="flex cursor-pointer flex-col items-center gap-3 rounded-md px-3 py-2 hover:bg-slate-100"
              onClick={action.onClick}
            >
              <div className="flex size-12 items-center justify-center rounded-full bg-white p-2 shadow-lg shadow-black/30">
                {action.image ? (
                  <img
                    src={action.image}
                    alt={action.title}
                    className="h-12 w-12 min-w-12 rounded-full"
                  />
                ) : (
                  action.icon && (
                    <action.icon className="size-6 text-foreground/80" />
                  )
                )}
              </div>
              <p className="text-center text-xs font-light">{action.title}</p>
            </div>
          ))}
        </div>
      </DrawerContent>
    </Drawer>
  );
};

export default ShareReferralDrawer;
